// #region Imports
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import RowActions from "_shared/_views/tables/row-actions";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
  searchFieldWidth,
  searchTextFieldProps,
} from "_shared/_views/tables/table-toolbar";
import TruncatedCell from "_shared/_views/tables/truncated-cell";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance,
  type MRT_TableOptions,
  type MRT_Updater,
} from "material-react-table";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
  StaffingCandidate,
  StaffingDestinationOrderField,
  StaffingOrderDirection,
  useAddToStaffingDestinationMutation,
  useGetStaffingDestinationsQuery,
} from "work/staffing/_api/work-staffing-api";
import { StaffingDestinationKind } from "work/staffing/_schemas/staffing-destination/staffing-destination-schema";
import { addToWork } from "work/staffing/_views/add-to-work-outcome";
import StaffingDestinationPage from "work/staffing/staffing-destination-page";
import StaffingDestination from "work/staffing/staffing-destination";
// #endregion

// #region Styled Components
const KindToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  alignSelf: "center",
  flexWrap: "wrap",
  maxWidth: "100%",
  "& .MuiToggleButtonGroup-grouped": {
    fontSize: "0.75rem",
    lineHeight: 1.2,
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
  },
}));

/**
 * The line under the table saying where in the set the reader is.
 *
 * MRT's page-number pager does not draw a range caption of its own, and the range is the half of
 * the answer the page numbers cannot give — in particular the `+` that says the count is a floor.
 */
const CountCaption = styled(Stack)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

/** The name cell: the work's name, with a mark on the rows that are already taken. */
const NameCellRow = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));

const AlreadyOnItChip = styled(Chip)(() => ({
  flexShrink: 0,
}));
// #endregion

/** How many destinations one page of the picker shows until the reader says otherwise. */
export const DEFAULT_PAGE_SIZE = 25;

/**
 * The page sizes on offer.
 *
 * Fifty is the top because it is the endpoint's own `MaximumPageSize`: a larger choice here would
 * be silently clamped by the server, and a pager that asks for a hundred and draws fifty is a
 * pager that lies about where the reader is.
 */
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

/**
 * How long the search box waits after a keystroke before the term reaches the server.
 *
 * Not this table's own timer: MRT's `MRT_GlobalFilterTextField` holds the typed value locally and
 * pushes it into `globalFilter` on a debounce of its own, 500ms while `manualFiltering` is on.
 * This names it so a spec can wait exactly as long as the reader does, and so the next person
 * does not add a second debounce on top of it — which is what the first version of this did.
 */
export const SEARCH_DEBOUNCE_MILLISECONDS = 500;

/** What the picker calls somebody who is already on the team it is offering. */
export const ALREADY_ON_THE_TEAM = "Already on this team";

/** The verb on each row's one action. */
export const ADD_TO_THIS_WORK = "Add to this work";

/** What the table lists, in the words its search box and its empty states are built from. */
export const DESTINATIONS_SUBJECT = "work to add them to";

/** What the search box is named, and therefore what a screen reader calls it. */
export const SEARCH_PLACEHOLDER = `Search ${DESTINATIONS_SUBJECT}`;

/**
 * The table's own accessible name.
 *
 * The surface around it cannot supply one. The dialog this is drawn in is the profile's, and it is
 * labelled by the profile's own title — correctly, because the profile is what the dialog is. The
 * panel names itself after the action, which is a second thing again. Naming the table is what
 * gives a reader, and a test, something unambiguous to address.
 */
export const DESTINATIONS_TABLE_LABEL = "Work you can add this person to";

/** What a viewer reads under the heading when they have no work to staff at all. */
export const NO_WORK_AT_ALL =
  "You are not leading any proposal or running work you can add somebody to.";

/** The sortable columns, by the ids the sort state carries. */
export const NAME_COLUMN = "name";
export const ACTIVE_SINCE_COLUMN = "activeSince";

/** What keys a row that is a loading placeholder rather than a destination. */
export const SKELETON_ROW_PREFIX = "skeleton-";

/**
 * The key MRT files each row under.
 *
 * **Not every row MRT keys is a destination.** While `isLoading` is true and nothing has arrived
 * yet, MRT throws away the `data` it was given and substitutes one blank object per skeleton row,
 * assembled from the *column ids* rather than from the row type — `{ name: null, kind: null,
 * counterparty: null, activeSince: null }`, with no `id` on it at all. It then keys those through
 * this function like any other row. Reading `id.value` off one threw inside MRT's core row model,
 * which is upstream of every cell, so the whole picker went to the router's error boundary on
 * every first open rather than losing a single cell.
 *
 * So a placeholder is keyed by its position, which is the only thing that separates one from
 * another, and the prefix says which kind of row it was. A real row is keyed by kind and id
 * together: the two kinds are two different stores and an id from one says nothing about the
 * other. A destination parsed from the API always has an id — the schema will not produce one
 * without — so nothing here is covering for a row that is merely missing a field.
 */
export function staffingRowId(row: StaffingDestination, index: number): string {
  // Typed as a destination because that is what this table lists. MRT's placeholder is not one,
  // and this is the one place that has to admit it.
  const id: Guid | undefined = (row as Partial<StaffingDestination>).id;
  return id ? `${row.kind}:${id.value}` : `${SKELETON_ROW_PREFIX}${index}`;
}

/** Sorted by name, ascending — the order a picker is read in, and the server's own default. */
export const DEFAULT_SORTING: SortingState = [{ id: NAME_COLUMN, desc: false }];

const columnHelper = createMRTColumnHelper<StaffingDestination>();

/**
 * The order the server is asked for, from the order the table is showing.
 *
 * Always one column and always a direction: the endpoint sorts every answer, so an "unsorted"
 * state would be a header arrow that means nothing. `enableSortingRemoval` is off for the same
 * reason.
 */
export function staffingOrderFrom(sorting: SortingState): {
  orderBy: StaffingDestinationOrderField;
  orderDir: StaffingOrderDirection;
} {
  const first = sorting.length > 0 ? sorting[0] : undefined;
  return {
    orderBy: first && first.id === ACTIVE_SINCE_COLUMN ? "ActiveSince" : "Name",
    orderDir: first && first.desc ? "Descending" : "Ascending",
  };
}

/** MRT hands state changes as either a value or a function of the old one. */
function applyUpdater<TState>(updater: MRT_Updater<TState>, current: TState): TState {
  return typeof updater === "function" ? (updater as (old: TState) => TState)(current) : updater;
}

function NameCell({
  row,
  table,
}: Readonly<{ row: MRT_Row<StaffingDestination>; table: MRT_TableInstance<StaffingDestination> }>) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  const destination = row.original;
  return (
    <NameCellRow>
      <TruncatedCell>
        <SearchMatch table={table} text={destination.name} />
      </TruncatedCell>
      {destination.candidateAlreadyBelongs && (
        <AlreadyOnItChip label={ALREADY_ON_THE_TEAM} size="small" variant="outlined" />
      )}
    </NameCellRow>
  );
}

function KindCell({ row }: Readonly<{ row: MRT_Row<StaffingDestination> }>) {
  return <TruncatedCell>{row.original.kindLabel}</TruncatedCell>;
}

function CounterpartyCell({ row }: Readonly<{ row: MRT_Row<StaffingDestination> }>) {
  return <TruncatedCell>{row.original.counterpartyLabel}</TruncatedCell>;
}

function ActiveSinceCell({ row }: Readonly<{ row: MRT_Row<StaffingDestination> }>) {
  return <TruncatedCell component="time">{row.original.activeSinceLabel}</TruncatedCell>;
}

export type StaffingDestinationsTableProps = {
  /** Who is being considered, as both halves of the seat a work team holds. */
  candidate: StaffingCandidate;
  /** Called once a destination has been acted on, so the surface holding this can close. */
  onChosen?: () => void;
};

/**
 * Every piece of work the viewer may put this person onto, as a table they can work through.
 *
 * **A table rather than a list, for the reason the repository has tables at all.** A leader can be
 * on hundreds of live proposals and running agreements, half of them named after the same client;
 * a ten-row list with Previous and Next asks them to make twenty presses to reach page twenty and
 * gives them a name and the word "Proposal" to choose by. This follows the app's own table
 * standards — [table toolbars](../../../../../docs/software-design/table-toolbars.md),
 * [columns](../../../../../docs/software-design/table-columns.md),
 * [row actions](../../../../../docs/software-design/table-row-actions.md) and
 * [empty states](../../../../../docs/software-design/table-empty-states.md) — with the in-pane
 * toolbar arrangement, because this is drawn in a slide-out panel rather than on a page.
 *
 * **Each column says what it holds, and the shared fit sizes it** (#6708, #6721). A heading is its
 * column's minimum, the name takes the 80th percentile of its rows, capped, and the panel's spare
 * width is shared in proportion. The name once had a fixed 265px, which the panel could not always
 * spare beside the other three. Resizing, and a double-click back to the fitted width, come with
 * the fit; the reader can put a column away entirely from the toolbar.
 *
 * **Everything that narrows or orders it is the server's work.** The term, the kind, the column,
 * the direction and the page all travel to `work/staffing-destinations`. That is not a preference:
 * the endpoint evaluates eligibility over a bounded window of each store, so the order decides
 * which rows are inside that window — sorting a fetched page would reorder ten rows out of two
 * hundred and present it as "oldest first".
 *
 * Choosing an entry seats the person directly rather than inviting them. Both rosters it offers are
 * the leader's own to compose, which is what separates this from Invite to Team next to it.
 */
export default function StaffingDestinationsTable(
  props: Readonly<StaffingDestinationsTableProps>,
) {
  const candidate = props.candidate;
  const onChosen = props.onChosen;

  const [term, setTerm] = useState("");
  const [kind, setKind] = useState<StaffingDestinationKind | "All">("All");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const navigate = useNavigate();

  const order = staffingOrderFrom(sorting);
  const results = useGetStaffingDestinationsQuery({
    candidate,
    search: term,
    kind: kind === "All" ? undefined : kind,
    orderBy: order.orderBy,
    orderDir: order.orderDir,
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
  });
  const [addToStaffingDestination] = useAddToStaffingDestinationMutation();

  const resultsData = results.data;
  const page = resultsData ? StaffingDestinationPage.fromJSON(resultsData) : null;
  // Freshly allocated when there is no answer yet, so it is built once here rather than inline in
  // the options object, where MRT would see a new array identity on every render.
  const rows = page ? [...page.destinations] : [];

  const showFirstPage = (next: Partial<PaginationState>) =>
    // A new result set is a new first page. Otherwise a reader who narrows the search lands on
    // page four of something whose page one they never saw.
    setPagination((current) => ({ ...current, pageIndex: 0, ...next }));

  const chooseDestination = async (destination: StaffingDestination) => {
    onChosen?.();
    await addToWork(addToStaffingDestination, candidate, destination, navigate);
  };

  const tableOptions: MRT_TableOptions<StaffingDestination> = {
    columns: [
      columnHelper.accessor((row) => row.name, {
        header: "Name",
        id: NAME_COLUMN,
        enableSorting: true,
        meta: { kind: "name" },
        Cell: NameCell,
      }),
      columnHelper.accessor((row) => row.kindLabel, {
        header: "Kind",
        id: "kind",
        // The server searches names only (#6590), so a match is never marked in the other columns.
        enableGlobalFilter: false,
        // There is a kind filter in the toolbar, which is the control for this column and a better
        // one: sorting by kind would only ever stack two lists.
        enableSorting: false,
        meta: { kind: "text" },
        Cell: KindCell,
      }),
      columnHelper.accessor((row) => row.counterpartyLabel, {
        header: "Counterparty",
        id: "counterparty",
        enableGlobalFilter: false,
        // Not sortable, and deliberately. The name is resolved from the LegalEntities center after
        // the page has been cut out of the eligibility window, so no store can order by it —
        // offering the header arrow would sort one page and call it the set.
        enableSorting: false,
        meta: { kind: "text" },
        Cell: CounterpartyCell,
      }),
      columnHelper.accessor((row) => row.activeSinceLabel, {
        header: "Active since",
        id: ACTIVE_SINCE_COLUMN,
        enableGlobalFilter: false,
        enableSorting: true,
        meta: { kind: "date" },
        Cell: ActiveSinceCell,
      }),
    ],
    data: rows,
    getRowId: staffingRowId,
    // The one thing a row can have done to it, and it has nowhere else to live: this table IS the
    // way to seat somebody from a profile. See table row actions.
    enableRowActions: true,
    // The row itself does nothing. Elsewhere a row opens a record, so a press is cheap and
    // reversible; here it would add a person to a team, which is neither, and a mis-click during a
    // scan is exactly what a hundred-row table invites.
    enableRowSelection: false,
    enableColumnFilters: false,
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableMultiSort: false,
    // The server sorts every answer, so there is no unsorted state for a third click to mean.
    enableSortingRemoval: false,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enablePagination: true,
    // Page numbers, not just chevrons. Reaching page twenty of two hundred was twenty presses.
    paginationDisplayMode: "pages",
    positionGlobalFilter: "none",
    positionActionsColumn: "last",
    positionToolbarAlertBanner: "none",
    rowCount: page ? page.totalCount : 0,
    pageCount: page ? page.pageCount : 1,
    initialState: { showGlobalFilter: true },
    displayColumnDefOptions: {
      "mrt-row-actions": { enableResizing: false },
    },
    muiPaginationProps: {
      color: "primary",
      rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
      shape: "rounded",
      showFirstButton: true,
      showLastButton: true,
      showRowsPerPage: true,
      size: "small",
      variant: "outlined",
    },
    muiTableContainerProps: {
      sx: {
        // The body scrolls, the header stays, and the height is whatever the panel has left after
        // its toolbar and its pager — not a fraction of the window. A second answer here would
        // give the panel one scrollbar and the table another inside it, which is a reader scrolling
        // twice to reach the same row.
        flex: "1 1 auto",
        minWidth: 0,
      },
    },
    muiTableProps: { "aria-label": DESTINATIONS_TABLE_LABEL },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    // MRT lays its top toolbar out as a grid whose inner flex Box keeps `min-width: auto`, so the
    // track sizes to that Box's content and the toolbar — which hides its overflow — clips whatever
    // runs past the edge, with no scrollbar to say so. A zero floor is what lets the one wrapping
    // row wrap at all. See docs/software-design/table-toolbars.md.
    muiTopToolbarProps: { sx: { "& > .MuiBox-root": { minWidth: 0 } } },
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({
        searches: DESTINATIONS_SUBJECT,
        table,
        onClear: () => {
          setTerm("");
          showFirstPage({});
        },
      }),
      size: "small" as const,
      sx: searchFieldWidth,
      variant: "outlined" as const,
    }),
    renderEmptyRowsFallback: emptyRowsFallback<StaffingDestination>({
      subject: DESTINATIONS_SUBJECT,
      // `isFetching`, not `isLoading`: every page, sort and keystroke empties the table while it is
      // in flight, and a message drawn then says "nothing matched" about rows already on their way.
      isLoading: results.isFetching,
      isError: results.isError,
      onRetry: () => void results.refetch(),
      // The kind filter is the table's own, not MRT's, so the empty state cannot see it. Without
      // this, narrowing to Proposals with none of them reads as an account with no work in it.
      hasPageFilters: kind !== "All",
      // Only shown where nothing is narrowing the list, which is the state it describes.
      description: NO_WORK_AT_ALL,
    }),
    renderRowActions: ({ row }) => (
      <RowActions
        ariaLabel={`Actions for ${row.original.name}`}
        actions={[
          {
            key: "add",
            label: ADD_TO_THIS_WORK,
            ariaLabel: `Add to ${row.original.name}`,
            icon: <PersonAddAltIcon />,
            disabled: row.original.candidateAlreadyBelongs,
            disabledReason: ALREADY_ON_THE_TEAM,
            onClick: () => void chooseDestination(row.original),
          },
        ]}
      />
    ),
    // One wrapping row: the search, then the way of narrowing, then the view buttons at the far
    // end. No breakpoint decides it — the panel is as wide as the dialog allows and the dialog
    // can be taken full screen, so wrapping is the browser answering "is there room" from the real
    // width. At the panel's own width the three sit on one row: the search asks for 280px, the kind
    // filter comes to about 230 and the columns button to 40.
    renderTopToolbarCustomActions: ({ table }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <KindToggleButtonGroup
          aria-label="Kind of work"
          color="primary"
          exclusive
          size="small"
          value={kind}
          onChange={(_event, next: StaffingDestinationKind | "All" | null) => {
            if (!next) return;
            setKind(next);
            showFirstPage({});
          }}
        >
          <ToggleButton aria-label="All work" value="All">
            All
          </ToggleButton>
          <ToggleButton aria-label="Proposals only" value="Proposal">
            Proposals
          </ToggleButton>
          <ToggleButton aria-label="Active work agreements only" value="WorkAgreement">
            Active work
          </ToggleButton>
        </KindToggleButtonGroup>
        <CollectionToolbarViewButtons>
          {/* Columns only. There are no column filters — the one thing worth narrowing by is the
              name, which the search does on the server, and the kind, which has its own control
              above. Full screen belongs to the dialog around this, and two of them in one surface is
              a reader pressing the wrong one. */}
          <TableToolsMenu table={table} offersFullScreen={false} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    // An empty fragment rather than `null`: MRT reads this with `??`, so a nullish answer restores
    // its own default button set and the toolbar ends up with two of everything.
    renderToolbarInternalActions: () => <></>,
    renderBottomToolbarCustomActions: () => (
      <CountCaption spacing={0}>
        <Typography color="text.secondary" variant="caption">
          {page ? page.countLabel : ""}
        </Typography>
        {/* The `+` says the number is a floor; this says what to do about it. The endpoint stopped
            evaluating at its eligibility window, and a narrower search is the only way past it. */}
        {page && page.truncationHint.length > 0 && (
          <Typography color="text.secondary" variant="caption">
            {page.truncationHint}
          </Typography>
        )}
      </CountCaption>
    ),
    onGlobalFilterChange: (updater: MRT_Updater<string>) => {
      setTerm(applyUpdater(updater, term) ?? "");
      showFirstPage({});
    },
    onPaginationChange: (updater: MRT_Updater<PaginationState>) => {
      const next = applyUpdater(updater, pagination);
      // A different page size is a different set of pages, so the reader goes back to the first
      // rather than to whatever row number their old page index now points at.
      setPagination(
        next.pageSize === pagination.pageSize ? next : { ...next, pageIndex: 0 },
      );
    },
    onSortingChange: (updater: MRT_Updater<SortingState>) => {
      const next = applyUpdater(updater, sorting);
      setSorting(next.length > 0 ? next : DEFAULT_SORTING);
      showFirstPage({});
    },
    state: {
      globalFilter: term,
      isLoading: results.isLoading,
      pagination,
      showGlobalFilter: true,
      showProgressBars: results.isFetching,
      sorting,
    },
  };

  return <MRTNoMemo<StaffingDestination> options={tableOptions} />;
}
